import { useCallback, useEffect, useRef, useState } from "react";
import { getFirestore, doc, getDoc, getDocs, collection } from "firebase/firestore";
import { useParams } from "src/hooks/useParams";
import uuidv4 from "src/utils/uuidv4";
import { nextActionType } from "src/types/video";
import { Box, CircularProgress, Typography } from "@mui/material";
import Iconify from "src/components/Iconify";

interface ChapterData {
  id: string;
  video_url: string;
  video_title: string;
  thumbnail: string;
  chapter_description?: string;
  duration?: number;
  cta?: nextActionType | null;
  subtitle_url?: string | null;
  subtitle_text_color?: string | null;
  subtitle_background_color?: string | null;
  subtitle_font_size?: number;
  subtitle_bold?: boolean;
  subtitle_font_family?: string | null;
}

const parseTimestamp = (timestamp: string) => {
  const [hours, minutes, seconds] = timestamp.split(":").map(parseFloat);
  return hours * 3600 + minutes * 60 + seconds;
};

const DEFAULT_MUTE_NOTICE_DURATION_MS = 3000;
const normalizeMuteNoticeDuration = (value: unknown) => {
  const duration = Number(value);
  if (!Number.isFinite(duration)) return DEFAULT_MUTE_NOTICE_DURATION_MS;
  return Math.min(Math.max(duration, 500), 10_000);
};

export default function VideoChooseV3() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toolbarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const params = useParams();
  const { videoChooseId } = params;
  const searchParams = new URLSearchParams(window.location.search);
  const isFirstAutoPlay = searchParams.get("autoplay") === "true";
  const waitForMuteNotice = searchParams.get("waitForMuteNotice") === "true";
  const miniButtonBgColor = searchParams.get("miniButtonBgColor") || "rgba(0,0,0,0.45)";
  const accentColor = searchParams.get("accentColor") || "#F97316";
  // iframeの外 = 管理画面プレビューなどの直接アクセス（本番はembed-tagのiframe内なのでfalse）
  const isPreview = window.self === window.top;

  const [consumerToken] = useState(() => {
    const storedToken = localStorage.getItem("consumerToken");
    if (storedToken) return storedToken;

    const newToken = uuidv4();
    localStorage.setItem("consumerToken", newToken);
    return newToken;
  });
  const [visitToken] = useState(uuidv4);

  const [chapters, setChapters] = useState<ChapterData[]>([]);
  const [measurementTag, setMeasurementTag] = useState<string>("");
  const [domain, setDomain] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [videoLoading, setVideoLoading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(isFirstAutoPlay);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [progress, setProgress] = useState<number>(0);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  // タップ時のみ中央コントロールを表示
  const [showControls, setShowControls] = useState<boolean>(false);
  // ツールバー表示（初期3秒表示、タップで再表示）
  const [showToolbar, setShowToolbar] = useState<boolean>(true);
  // フルスクリーン状態
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  // 動画終了フラグ
  const [isEnded, setIsEnded] = useState<boolean>(false);
  // ミュート通知（初回autoplay時のみ）
  const [showMuteNotice, setShowMuteNotice] = useState<boolean>(false);
  const [muteNoticeDurationMs, setMuteNoticeDurationMs] = useState<number>(
    DEFAULT_MUTE_NOTICE_DURATION_MS,
  );
  const muteNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitForMuteNoticeRef = useRef<boolean>(waitForMuteNotice);
  const initialAutoplayAttemptedRef = useRef<boolean>(false);
  const [playAfterMuteNotice, setPlayAfterMuteNotice] = useState<boolean>(false);
  // PCホバー状態
  const [isHovered, setIsHovered] = useState<boolean>(false);
  // SPデバイス判定（embed-tagから受信）
  const [isSPDevice, setIsSPDevice] = useState<boolean>(false);
  // ミニプレーヤー状態（embed-tagから受信）
  const [isMiniPlayer, setIsMiniPlayer] = useState<boolean>(false);
  // コンテナ・動画サイズ（レターボックス計算用）
  const containerRef = useRef<HTMLDivElement>(null);
  const [videoNaturalSize, setVideoNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  // 初回vc_play送信フラグ
  const isFirstPlaySentRef = useRef<boolean>(false);
  // 字幕
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [subtitleEntries, setSubtitleEntries] = useState<
    { start: number; end: number; text: string }[]
  >([]);

  const currentChapter = chapters[currentIndex] ?? null;
  const currentSubtitle = subtitleEntries.find(
    (s) => currentTime >= s.start && currentTime <= s.end,
  );

  const sendInitialPlay = useCallback(() => {
    if (isFirstPlaySentRef.current) return;
    isFirstPlaySentRef.current = true;
    window.parent.postMessage({ action: "vc_play", videoChooseId, domain }, "*");
    window.parent.postMessage(
      {
        action: "setVcCvCookie",
        measurementTag: "via-video-only",
        videoChooseId,
        domain,
        consumerToken,
        visitToken,
      },
      "*",
    );
  }, [consumerToken, domain, videoChooseId, visitToken]);

  // Firestoreからチャプターデータ取得
  useEffect(() => {
    const fetchData = async () => {
      if (!videoChooseId) return;
      setLoading(true);
      try {
        const db = getFirestore();
        const docRef = doc(db, "videoChooses", videoChooseId);
        const [docSnap, videosSnapshot] = await Promise.all([
          getDoc(docRef),
          getDocs(collection(docRef, "videos")),
        ]);
        if (!docSnap.exists()) return;

        const docData = docSnap.data();
        const order: string[] = docData.order || [];
        const measurementTag: string = docData.measurementTag || "";
        const domain: string = docData.domain || "";
        const defaultSpeed: number = docData.default_speed || 1;
        setPlaybackRate(defaultSpeed);

        const videosMap: Record<string, ChapterData> = {};
        videosSnapshot.docs.forEach((videoDoc) => {
          const data = videoDoc.data();
          videosMap[videoDoc.id] = {
            id: videoDoc.id,
            video_url: data.video_url,
            video_title: data.video_title,
            thumbnail: data.thumbnail,
            chapter_description: data.chapter_description || "",
            duration: data.duration ?? undefined,
            subtitle_url: data.subtitle_url || null,
            subtitle_text_color: data.subtitle_text_color || null,
            subtitle_background_color: data.subtitle_background_color || null,
            subtitle_font_size: data.subtitle_font_size || 13,
            subtitle_bold: data.subtitle_bold || false,
            subtitle_font_family: data.subtitle_font_family || null,
          };
        });

        const ordered = order.map((id) => videosMap[id]).filter(Boolean);

        // タイトル・サムネ・durationだけで即表示（CTAはバックグラウンドで後から取得）
        setChapters(ordered);

        // durationがない場合は動画メタデータから取得（フォールバック）
        ordered.forEach((ch) => {
          if (ch.duration) return;
          const vid = document.createElement("video");
          vid.preload = "metadata";
          vid.onloadedmetadata = () => {
            setChapters((prev) =>
              prev.map((c) => (c.id === ch.id ? { ...c, duration: Math.round(vid.duration) } : c)),
            );
            vid.src = "";
          };
          vid.onerror = () => {
            vid.src = "";
          };
          vid.src = ch.video_url;
        });
        setMeasurementTag(measurementTag);
        setDomain(domain);

        // nextActions（CTA）をバックグラウンドで取得してチャプターを更新
        void Promise.all(
          ordered.map(async (ch) => {
            const videoDocRef = doc(db, "videoChooses", videoChooseId, "videos", ch.id);
            const nextActionsSnap = await getDocs(collection(videoDocRef, "nextActions"));
            if (nextActionsSnap.empty) return null;

            const firstDoc = nextActionsSnap.docs[0];
            return [ch.id, { id: firstDoc.id, ...firstDoc.data() } as nextActionType] as const;
          }),
        )
          .then((results) => {
            const ctas = new Map(
              results.filter(
                (result): result is readonly [string, nextActionType] => result !== null,
              ),
            );
            if (ctas.size === 0) return;
            setChapters((prev) =>
              prev.map((chapter) => {
                const cta = ctas.get(chapter.id);
                return cta ? { ...chapter, cta } : chapter;
              }),
            );
          })
          .catch((error) => console.error(error));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [videoChooseId]);

  // チャプターリストをembed-tagへ送信
  useEffect(() => {
    if (chapters.length === 0) return;
    window.parent.postMessage(
      {
        action: "vc_chapters",
        chapters: chapters.map((c) => ({
          id: c.id,
          video_title: c.video_title,
          thumbnail: c.thumbnail,
          chapter_description: c.chapter_description,
          duration: c.duration,
          cta: c.cta ?? null,
        })),
        videoChooseId,
        measurementTag,
        consumerToken,
        visitToken,
        domain,
      },
      "*",
    );
  }, [chapters, videoChooseId, measurementTag, consumerToken, visitToken, domain]);

  // embed-tagからのメッセージ受信
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // 埋め込み元以外のwindowからの操作は受け付けない。
      if (event.source !== window.parent || !event.data) return;
      const { action, videoId } = event.data;
      if (action === "vc_play_chapter" && videoId) {
        const idx = chapters.findIndex((c) => c.id === videoId);
        if (idx !== -1) setCurrentIndex(idx);
      } else if (action === "vc_play") {
        videoRef.current?.play()?.catch(() => {
          window.parent.postMessage({ action: "vc_autoplay_failed", videoChooseId }, "*");
        });
        sendInitialPlay();
      } else if (action === "vc_play_unmuted") {
        // 低電力モード時にユーザーが明示的に再生ボタンをタップした場合（音声あり）
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.play()?.catch(() => {
            window.parent.postMessage({ action: "vc_autoplay_failed", videoChooseId }, "*");
          });
        }
        setIsMuted(false);
        sendInitialPlay();
      } else if (action === "vc_pause") {
        videoRef.current?.pause();
      } else if (action === "vc_fullscreen_change") {
        setIsFullscreen(!!event.data.isFullscreen);
      } else if (action === "vc_device_info") {
        setIsSPDevice(!!event.data.isSP);
      } else if (action === "vc_mini_on") {
        setIsMiniPlayer(true);
      } else if (action === "vc_mini_off") {
        setIsMiniPlayer(false);
      } else if (action === "vc_show_mute_notice") {
        setMuteNoticeDurationMs(normalizeMuteNoticeDuration(event.data.durationMs));
        setPlayAfterMuteNotice(event.data.playAfterMuteNotice === true);
        setShowMuteNotice(true);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [chapters, sendInitialPlay]);

  // チャプター切り替え時に動画をロード・再生
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !currentChapter) return;

    setVideoLoading(true);
    setProgress(0);
    setCurrentTime(0);
    setIsEnded(false);
    videoEl.src = currentChapter.video_url;
    videoEl.playbackRate = playbackRate;
    videoEl.muted = isMuted;

    const handleLoadedMetadata = () => {
      setVideoDuration(videoEl.duration);
      // videoWidthは0になる場合があるのでcanPlayでも取得する
      if (videoEl.videoWidth && videoEl.videoHeight) {
        setVideoNaturalSize({ w: videoEl.videoWidth, h: videoEl.videoHeight });
      }
    };
    const handleCanPlay = () => {
      setVideoLoading(false);
      // loadedmetadataで取得できなかった場合のフォールバック
      if (videoEl.videoWidth && videoEl.videoHeight) {
        setVideoNaturalSize({ w: videoEl.videoWidth, h: videoEl.videoHeight });
      }
    };
    const handleWaiting = () => setVideoLoading(true);
    const handleError = () => setVideoLoading(false);
    const handleTimeUpdate = () => {
      setCurrentTime(videoEl.currentTime);
      if (videoEl.duration) {
        setProgress(videoEl.currentTime / videoEl.duration);
      }
    };
    const handlePlay = () => {
      setIsPlaying(true);
      window.parent.postMessage(
        {
          action: "vc_video_play",
          videoId: currentChapter.id,
          videoChooseId,
          domain,
          currentTime: videoEl.currentTime,
        },
        "*",
      );
    };
    const handlePause = () => {
      setIsPlaying(false);
      window.parent.postMessage(
        {
          action: "vc_video_pause",
          videoId: currentChapter.id,
          videoChooseId,
          domain,
          currentTime: videoEl.currentTime,
        },
        "*",
      );
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setIsEnded(true);
      window.parent.postMessage(
        {
          action: "vc_video_ended",
          videoId: currentChapter.id,
          videoChooseId,
          domain,
          currentTime: videoEl.duration || 0,
        },
        "*",
      );
    };

    videoEl.addEventListener("loadedmetadata", handleLoadedMetadata);
    videoEl.addEventListener("canplay", handleCanPlay);
    videoEl.addEventListener("waiting", handleWaiting);
    videoEl.addEventListener("error", handleError);
    videoEl.addEventListener("timeupdate", handleTimeUpdate);
    videoEl.addEventListener("play", handlePlay);
    videoEl.addEventListener("pause", handlePause);
    videoEl.addEventListener("ended", handleEnded);

    if (!loading && !waitForMuteNoticeRef.current) {
      const isInitialAutoplay = !initialAutoplayAttemptedRef.current;
      initialAutoplayAttemptedRef.current = true;
      videoEl.play().catch(() => {
        if (isInitialAutoplay) {
          window.parent.postMessage({ action: "vc_autoplay_failed", videoChooseId }, "*");
        }
      });
      sendInitialPlay();
    }

    return () => {
      videoEl.removeEventListener("loadedmetadata", handleLoadedMetadata);
      videoEl.removeEventListener("canplay", handleCanPlay);
      videoEl.removeEventListener("waiting", handleWaiting);
      videoEl.removeEventListener("error", handleError);
      videoEl.removeEventListener("timeupdate", handleTimeUpdate);
      videoEl.removeEventListener("play", handlePlay);
      videoEl.removeEventListener("pause", handlePause);
      videoEl.removeEventListener("ended", handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, currentChapter?.id, loading]);

  const finishMuteNotice = useCallback(
    (unmute = false) => {
      if (unmute) setIsMuted(false);
      setShowMuteNotice(false);
      waitForMuteNoticeRef.current = false;
      if (playAfterMuteNotice) {
        initialAutoplayAttemptedRef.current = true;
        videoRef.current?.play().catch(() => {
          window.parent.postMessage({ action: "vc_autoplay_failed", videoChooseId }, "*");
        });
        sendInitialPlay();
      }
    },
    [playAfterMuteNotice, sendInitialPlay],
  );

  // ツールバーを初期3秒後に自動非表示

  useEffect(() => {
    toolbarTimerRef.current = setTimeout(() => setShowToolbar(false), 3000);
    return () => {
      if (toolbarTimerRef.current) clearTimeout(toolbarTimerRef.current);
    };
  }, []);

  // ミュート通知をembed-tagから指定された時間後に自動消去
  useEffect(() => {
    if (!showMuteNotice) return;
    muteNoticeTimerRef.current = setTimeout(() => {
      finishMuteNotice();
    }, muteNoticeDurationMs);
    return () => {
      if (muteNoticeTimerRef.current) clearTimeout(muteNoticeTimerRef.current);
    };
  }, [showMuteNotice, muteNoticeDurationMs, finishMuteNotice]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = isMuted;
  }, [isMuted]);

  // 字幕JSONフェッチ（チャプター切り替え時）
  useEffect(() => {
    setSubtitleEntries([]);
    const url = currentChapter?.subtitle_url;
    if (!url) return;

    const controller = new AbortController();
    let isCurrentRequest = true;

    fetch(url, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        if (!isCurrentRequest || !Array.isArray(data)) return;
        const parsed = data
          .filter((item) => typeof item === "object" && item !== null)
          .map((item) => ({
            start: parseTimestamp(item.start),
            end: parseTimestamp(item.end),
            text: item.text as string,
          }));
        setSubtitleEntries(parsed);
      })
      .catch((error) => {
        if (error.name !== "AbortError") console.error(error);
      });

    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [currentChapter?.subtitle_url]);

  // コンテナサイズの監視（レターボックス計算用）
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    const obs = new ResizeObserver(() => {
      setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // 字幕フォントファミリー適用
  useEffect(() => {
    const family = currentChapter?.subtitle_font_family;
    if (!family) return;
    const fontFaceMap: Record<string, string> = {
      "Noto Sans JP": `@font-face { font-family: "Noto Sans JP"; src: url("${process.env.PUBLIC_URL}/assets/fonts/NotoSansJP-Regular.ttf") format("truetype"); font-weight: 400; font-style: normal; }`,
      Tsunagi: `@font-face { font-family: "Tsunagi"; src: url("${process.env.PUBLIC_URL}/assets/fonts/TsunagiGothic.ttf") format("truetype"); font-weight: 400; font-style: normal; }`,
    };
    const style = document.createElement("style");
    style.textContent = `${fontFaceMap[family] || ""} .vc3-subtitle { font-family: ${family}, sans-serif !important; }`;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, [currentChapter?.subtitle_font_family]);

  // 中央コントロール＋ツールバーを一時表示して自動非表示
  const flashControls = useCallback(() => {
    setShowControls(true);
    setShowToolbar(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 2500);
    if (toolbarTimerRef.current) clearTimeout(toolbarTimerRef.current);
    toolbarTimerRef.current = setTimeout(() => setShowToolbar(false), 2500);
  }, []);

  const handleVideoAreaClick = () => {
    if (showControls) {
      // 表示中 → 即非表示（タイマーをキャンセル）
      setShowControls(false);
      setShowToolbar(false);
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
      if (toolbarTimerRef.current) clearTimeout(toolbarTimerRef.current);
    } else {
      // 非表示 → 2.5秒タイマー付きで表示
      flashControls();
    }
  };

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    flashControls();
  };

  const handleSeekBackward = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(videoRef.current.currentTime - 2.5, 0);
    }
    flashControls();
  };

  const handleSeekForward = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.currentTime + 2.5,
        videoRef.current.duration || 0,
      );
    }
    flashControls();
  };

  const handleSpeedChange = () => {
    const speeds = [1, 1.2, 1.5, 2];
    const next = speeds[(speeds.indexOf(playbackRate) + 1) % speeds.length];
    setPlaybackRate(next);
    window.parent.postMessage({ action: "vc_change_speed", videoChooseId }, "*");
  };

  const handleFullscreen = () => {
    if (isFullscreen) {
      window.parent.postMessage({ action: "vc_exit_fullscreen", videoChooseId }, "*");
    } else {
      window.parent.postMessage({ action: "vc_fullscreen", videoChooseId }, "*");
    }
  };

  const handleMiniEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.parent.postMessage({ action: "vc_mini_on", videoChooseId }, "*");
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current || !videoDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0) return;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    videoRef.current.currentTime = ratio * videoDuration;
    setProgress(ratio);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          bgcolor: "#000",
        }}
      >
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  if (!currentChapter) return null;

  // 動画がコンテナより横長の場合、上下にレターボックス（黒帯）が発生する。その下端オフセットを計算。
  const letterboxBottom = (() => {
    const vw = videoNaturalSize?.w || videoRef.current?.videoWidth || 0;
    const vh = videoNaturalSize?.h || videoRef.current?.videoHeight || 0;
    const cw = containerSize.w || containerRef.current?.clientWidth || 0;
    const ch = containerSize.h || containerRef.current?.clientHeight || 0;
    if (!vw || !vh || !cw || !ch) return 0;
    const videoAR = vw / vh;
    const containerAR = cw / ch;
    if (videoAR > containerAR) {
      const renderedH = cw / videoAR;
      return Math.max(0, (ch - renderedH) / 2);
    }
    return 0;
  })();

  const muteNoticeKeyframes = `
    @keyframes vc3-spreadwave {
      0% { width: 0; height: 0; opacity: 0.6; }
      100% { width: 800px; height: 800px; opacity: 0; }
    }
  `;

  const controlButtonBase = {
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backdropFilter: "blur(4px)",
    bgcolor: "rgba(0,0,0,0.25)",
  };

  return (
    <>
      <style>{muteNoticeKeyframes}</style>
      <Box
        ref={containerRef}
        onClick={handleVideoAreaClick}
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setIsHovered(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setIsHovered(false);
        }}
        sx={{
          position: "fixed",
          top: 0,
          bottom: 0,
          left: isPreview ? "50%" : 0,
          transform: isPreview ? "translateX(-50%)" : "none",
          width: "100%",
          maxWidth: isPreview ? 768 : "none",
          bgcolor: "#000",
          userSelect: "none",
          overflow: "hidden",
          cursor: "pointer",
        }}
      >
        {/* 動画エリア（iframe全体を使用） */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
          }}
        >
          <video
            ref={videoRef}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
            playsInline
            muted={isMuted}
          />

          {/* ミュート通知オーバーレイ */}
          {showMuteNotice && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                finishMuteNotice(true);
              }}
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.55)",
                zIndex: 25,
                cursor: "pointer",
                overflow: "hidden",
              }}
            >
              {/* 波紋 */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: 0,
                  height: 0,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.25)",
                  transform: "translate(-50%, -50%)",
                  animation: "vc3-spreadwave 1.5s infinite ease-out",
                }}
              />
              {/* コンテンツ */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* ミュートアイコン */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="52"
                  height="52"
                  viewBox="0 0 24 24"
                  style={{ fill: "#fff" }}
                >
                  <path d="M12.92 3.315C13.726 2.6 15 3.171 15 4.25v15.495c0 1.079-1.274 1.651-2.08.934l-4.492-3.993a.75.75 0 0 0-.498-.19H4.25A2.25 2.25 0 0 1 2 14.246V9.748a2.25 2.25 0 0 1 2.25-2.25h3.68a.75.75 0 0 0 .498-.19zm.58 1.491L9.425 8.43a2.25 2.25 0 0 1-1.495.568H4.25a.75.75 0 0 0-.75.75v4.498c0 .414.336.75.75.75h3.68a2.25 2.25 0 0 1 1.495.569l4.075 3.623zm2.72 4.413a.75.75 0 0 1 1.06 0L19 10.94l1.72-1.72a.75.75 0 1 1 1.06 1.06L20.06 12l1.72 1.72a.75.75 0 0 1-1.06 1.06L19 13.06l-1.72 1.72a.75.75 0 1 1-1.06-1.06L17.94 12l-1.72-1.72a.75.75 0 0 1 0-1.06" />
                </svg>
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  タップしてミュート解除
                </Typography>
              </Box>
            </Box>
          )}

          {/* チャプタータイトル */}
          <Box sx={{ position: "absolute", top: 8, left: 10, zIndex: 20 }}>
            <Typography sx={{ color: "#fff", fontSize: 11, lineHeight: 1.4 }}>
              {currentChapter.video_title}
            </Typography>
          </Box>

          {/* ローディング */}
          {videoLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "rgba(0,0,0,0.6)",
                zIndex: 15,
              }}
            >
              <CircularProgress sx={{ color: "#fff" }} size={36} />
            </Box>
          )}

          {/* 動画終了時リプレイ＋次へボタン */}
          {isEnded && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "20px",
                bgcolor: "rgba(0,0,0,0.45)",
                zIndex: 20,
              }}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                    videoRef.current.play();
                    setIsEnded(false);
                    window.parent.postMessage(
                      {
                        action: "vc_video_replay",
                        videoId: currentChapter.id,
                        videoChooseId,
                        domain,
                        currentTime: 0,
                      },
                      "*",
                    );
                  }
                }}
                sx={{
                  ...controlButtonBase,
                  width: 54,
                  height: 54,
                  opacity: 0.95,
                  cursor: "pointer",
                }}
              >
                <Iconify icon="mdi:replay" width={32} color="rgba(255,255,255,1)" />
              </Box>
              {isPreview && currentIndex < chapters.length - 1 && (
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex((prev) => prev + 1);
                    setIsEnded(false);
                  }}
                  sx={{
                    ...controlButtonBase,
                    width: 54,
                    height: 54,
                    opacity: 0.95,
                    cursor: "pointer",
                  }}
                >
                  <Iconify icon="mdi:skip-forward" width={32} color="rgba(255,255,255,1)" />
                </Box>
              )}
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              zIndex: 10,
              opacity: showControls || isHovered ? 1 : 0,
              transition: "opacity 0.25s ease",
              pointerEvents: showControls || isHovered ? "auto" : "none",
            }}
          >
            {/* 2.5秒戻る */}
            <Box
              onClick={handleSeekBackward}
              sx={{
                ...controlButtonBase,
                width: 42,
                height: 42,
                opacity: 0.9,
              }}
            >
              <Iconify icon="mdi:skip-previous" width={24} color="rgba(255,255,255,1)" />
            </Box>

            {/* 再生/一時停止 */}
            <Box
              onClick={handlePlayPause}
              sx={{
                ...controlButtonBase,
                width: 54,
                height: 54,
                opacity: 0.95,
              }}
            >
              <Iconify
                icon={isPlaying ? "mdi:pause" : "mdi:play"}
                width={32}
                color="rgba(255,255,255,1)"
              />
            </Box>

            {/* 2.5秒進む */}
            <Box
              onClick={handleSeekForward}
              sx={{
                ...controlButtonBase,
                width: 42,
                height: 42,
                opacity: 0.9,
              }}
            >
              <Iconify icon="mdi:skip-next" width={24} color="rgba(255,255,255,1)" />
            </Box>
          </Box>

          {/* 前チャプターボタン（プレビュー時のみ常時表示） */}
          {isPreview && currentIndex > 0 && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((prev) => prev - 1);
                setIsEnded(false);
              }}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 15,
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Iconify icon="mdi:chevron-left" width={26} color="rgba(255,255,255,0.9)" />
            </Box>
          )}
          {/* 次チャプターボタン（プレビュー時のみ常時表示） */}
          {isPreview && currentIndex < chapters.length - 1 && (
            <Box
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex((prev) => prev + 1);
                setIsEnded(false);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 15,
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Iconify icon="mdi:chevron-right" width={26} color="rgba(255,255,255,0.9)" />
            </Box>
          )}
        </Box>

        {/* 字幕オーバーレイ */}
        {currentSubtitle && (
          <Box
            className="vc3-subtitle"
            sx={{
              position: "absolute",
              // letterboxBottomを加算することで動画コンテンツ下端付近（動画に少し被る位置）に表示
              bottom: letterboxBottom + (showToolbar || isHovered ? 52 : 4),
              left: 0,
              right: 0,
              zIndex: 20,
              pointerEvents: "none",
              textAlign: "center",
              backgroundColor: currentChapter.subtitle_background_color || "rgba(20, 20, 20, 0.5)",
              color: currentChapter.subtitle_text_color || "#fff",
              padding: "5px 10px",
              fontSize: currentChapter.subtitle_font_size || 13,
              fontWeight: currentChapter.subtitle_bold ? 700 : 400,
            }}
          >
            {currentSubtitle.text}
          </Box>
        )}

        {/* プログレスバー（常時表示・ツールバー有無で位置が変わる） */}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleProgressClick(e);
          }}
          sx={{
            position: "absolute",
            bottom: letterboxBottom + (showToolbar || isHovered ? 57 : 0),
            left: 0,
            right: 0,
            height: 4,
            bgcolor: "rgba(255,255,255,0.25)",
            cursor: "pointer",
            zIndex: 30,
            transition: "bottom 0.25s ease",
          }}
        >
          <Box
            sx={{
              width: `${progress * 100}%`,
              height: "100%",
              bgcolor: accentColor,
              transition: "width 0.3s linear",
            }}
          />
        </Box>

        {/* ボトムバー（タップ時表示・フェードイン/アウト） */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            bottom: letterboxBottom,
            left: 0,
            right: 0,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: "rgba(15,15,15,0.6)",
            zIndex: 30,
            opacity: showToolbar || isHovered ? 1 : 0,
            pointerEvents: showToolbar || isHovered ? "auto" : "none",
            transition: "opacity 0.25s ease",
          }}
        >
          {/* ミュートボタン（サークル） */}
          <Box
            onClick={() => {
              setIsMuted((prev) => !prev);
              setShowMuteNotice(false);
            }}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "1.5px solid rgba(255,255,255,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Iconify
              icon={isMuted ? "fluent:speaker-mute-24-regular" : "fluent:speaker-2-16-regular"}
              width={28}
              color="#fff"
            />
          </Box>

          {/* 速度ボタン（サークル） */}
          <Box
            onClick={handleSpeedChange}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "1.5px solid rgba(255,255,255,0.75)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#fff" }}>
              {playbackRate}x
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* 拡大ボタン（ボーダーなし） */}
          <Box
            onClick={handleFullscreen}
            sx={{
              display: "none",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Iconify
              icon={
                isFullscreen ? "material-symbols:close-fullscreen" : "material-symbols:open-in-full"
              }
              width={33}
              color="rgba(255,255,255,0.85)"
            />
          </Box>

          {/* ミニプレーヤーボタン（ツールバー表示中のみ。非表示時は下の独立ボタンが担当） */}
          <Box
            onClick={
              isMiniPlayer
                ? () => window.parent.postMessage({ action: "vc_mini_off", videoChooseId }, "*")
                : handleMiniEnter
            }
            sx={{
              display: isSPDevice && (showToolbar || isHovered) ? "flex" : "none",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <Iconify
              icon={
                isMiniPlayer
                  ? "material-symbols:open-in-full"
                  : "material-symbols-light:pip-exit-outline"
              }
              width={isMiniPlayer ? 28 : 35}
              color="rgba(255,255,255,0.85)"
            />
          </Box>
        </Box>

        {/* 縮小ボタン単独表示（SP・ツールバー非表示時のみ） */}
        {(isMiniPlayer || (isSPDevice && !showToolbar && !isHovered)) && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (isMiniPlayer) {
                window.parent.postMessage({ action: "vc_mini_off", videoChooseId }, "*");
              } else {
                handleMiniEnter(e);
              }
            }}
            sx={{
              position: "absolute",
              bottom: letterboxBottom + 5,
              right: 7,
              zIndex: 30,
              width: 45,
              height: 45,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              bgcolor: miniButtonBgColor,
              borderRadius: "50%",
            }}
          >
            <Iconify
              icon={
                isMiniPlayer
                  ? "material-symbols:open-in-full"
                  : "material-symbols-light:pip-exit-outline"
              }
              width={isMiniPlayer ? 28 : 35}
              color="rgba(255,255,255,0.85)"
            />
          </Box>
        )}
      </Box>
    </>
  );
}
